import React, { useCallback, useEffect, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import { AllModules } from "@ag-grid-enterprise/all-modules";
import { ColDef } from "@ag-grid-enterprise/all-modules";
import { useAutorequestTraces } from "hooks/useAutorequestTraces";
import { useTemplateGridOptions } from "hooks/useTemplateGridOptions";
import { IdentifiableTraceInfo, QueryInfo } from "types";
import { dateComparator, diagnosticKey } from "utils/utils";
import TimeZonePicker from "./TimeStampHeader";
import TimeCellRenderer from "./TimeCellRenderer";
import { DateRangeFilterContext } from "utils/context";
import { getUnixTime } from "date-fns";
import { useSearchParams } from "react-router-dom";

type Props = {
    setDiagnostic: (s: IdentifiableTraceInfo) => void;
    queryInfo: QueryInfo | undefined;
};

enum SeverityEnum {
    Trace = 0,
    Info = 1,
    Warn = 2,
    Error = 3
}

// !todo: traces don't have a valid id on them which makes ag grid just assign an index to them by counting,
// this is an issue when a the number of traces change, for instance when filtering is applied.
// The current approach will select the previously selected log index, even though it isn't what we care about.

export default function DiagnosticsGrid({ setDiagnostic, queryInfo }: Props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const getSearchParam = () => {
        return searchParams.get(diagnosticKey);
    };
    const [gridOptions, gridApi] = useTemplateGridOptions(columnDefs, undefined, getSearchParam, true);
    const [traces, loading, error] = useAutorequestTraces(queryInfo);
    const [filterRange, setFilterRange] = useState<[Date | null, Date | null]>([null, null]);

    useEffect(() => {
        if (gridApi && gridOptions) {
            gridOptions.onSelectionChanged = (e) => {
                const selectedNodes = e.api.getSelectedNodes();
                if (selectedNodes && selectedNodes[0]) {
                    searchParams.set(diagnosticKey, btoa(selectedNodes[0].id));
                    setSearchParams(searchParams);
                    setDiagnostic(selectedNodes[0].data);
                }
            };
        }
    }, [gridApi, gridOptions, searchParams, setDiagnostic, setSearchParams]);

    useEffect(() => {
        if (gridApi) {
            if (loading) {
                gridApi.showLoadingOverlay();
            }

            if (!loading && traces) {
                gridApi.hideOverlay();
                gridApi.setRowData(traces);
            }

            //todo: figure out how to show error overlay
            // if (!error) {
            //     gridOptions.overlayNoRowsTemplate =
            //         '<span class="messageStyles">Problem with service link. Please try again later</span>';
            //     gridApi.showNoRowsOverlay();
            // }
        }
    }, [error, gridApi, gridOptions, loading, traces]);

    useEffect(() => {
        if (gridOptions) {
            const getContextMenuItems = () => {
                return ["copy", "copyWithHeaders"];
            };
            gridOptions.getContextMenuItems = getContextMenuItems;
        }
    }, [gridOptions, setDiagnostic]);

    const isFilter = useCallback(() => {
        const [to, from] = [...filterRange];
        return !!to || !!from;
    }, [filterRange]);

    const filterPass = useCallback(
        (node) => {
            let flag = true;
            const [from, to] = [...filterRange];
            const timestamp = new Date(node.data.timestamp).valueOf() / 1000;

            if (from && timestamp < getUnixTime(from)) {
                flag = false;
            }
            if (to && timestamp > getUnixTime(to)) {
                flag = false;
            }

            return flag;
        },
        [filterRange]
    );

    useEffect(() => {
        if (gridOptions) {
            gridOptions.isExternalFilterPresent = isFilter;
            gridOptions.doesExternalFilterPass = filterPass;
        }
    }, [gridOptions, filterRange, isFilter, filterPass]);

    useEffect(() => {
        if (gridApi) {
            if (filterRange) {
                gridApi.onFilterChanged();
            }
        }
    }, [gridApi, filterRange]);

    useEffect(() => {
        if (gridOptions) {
            const getContextMenuItems = (params: any) => {
                // make sure the correct node is selected on left-click before
                // copying the link to clipboard.
                params.node.setSelected(true);
                return [
                    // {
                    //     name: "Copy Link",
                    //     action: () => {
                    //         navigator.clipboard.writeText(window.location.href);
                    //         CopyToaster.show({
                    //             message: `Copied to Clipboard`,
                    //             intent: "success",
                    //             timeout: 1500
                    //         });
                    //     },
                    //     icon: "<span class='bp3-icon-standard bp3-icon-link bp3-intent-primary'></span>"
                    // }
                ];
            };
            gridOptions.getContextMenuItems = getContextMenuItems;
        }
    }, [gridOptions]);

    return (
        <DateRangeFilterContext.Provider value={[filterRange, setFilterRange]}>
            <AgGridReact
                modules={AllModules}
                gridOptions={gridOptions}
                frameworkComponents={{ customTimeZone: TimeZonePicker, timeZoneCell: TimeCellRenderer }}
            />
        </DateRangeFilterContext.Provider>
    );
}
const columnDefs: ColDef[] = [
    {
        colId: "id",
        headerName: "Id",
        field: "id",
        cellClass: "no-border",
        hide: true
        // cellRenderer: "companyCellRenderer"
    },
    {
        colId: "timestamp",
        headerName: "Time Stamp",
        field: "timestamp",
        cellClass: "no-border",
        sort: "asc",
        comparator: dateComparator,
        headerComponent: "customTimeZone",
        cellRenderer: "timeZoneCell"
    },

    {
        colId: "category",
        headerName: "Category",
        field: "category",
        cellClass: "no-border"
    },
    {
        headerName: "LogLevel",
        field: "severityLevel",
        valueFormatter: (p) => {
            return SeverityEnum[p.value];
        },
        cellStyle: (params) => {
            //todo: move these to cellCallsRules
            switch (params.value) {
                case SeverityEnum.Info:
                    return { color: "#F5F8FA", backgroundColor: "#2B95D6" };
                case SeverityEnum.Trace:
                    return { color: "#F5F8FA", backgroundColor: "#106BA3" };
                case SeverityEnum.Warn:
                    return { color: "#F5F8FA", backgroundColor: "#F29D49" };
                case SeverityEnum.Error:
                    return { color: "#F5F8FA", backgroundColor: "#F55656" };
                default:
                    return null;
            }
        },
        cellClass: "no-border"
    },
    {
        colId: "jobId",
        headerName: "Job",
        field: "jobId",
        cellClass: "no-border",
        flex: 1
    }
];
